/**
 * i18n.ts — Classveew Frontend Internationalisation
 *
 * DESIGN: Zero hardcoded language codes.
 *   - Which languages exist       → driven by /i18n/languages.json (CDN or bundled)
 *   - Which languages are RTL     → driven by the "dir" field in languages.json
 *   - Which translation to load   → driven by language code from languages.json
 *   - Adding a new language       → add entry to languages.json + upload {code}.json to CDN
 *                                   ZERO code changes needed here.
 */
import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
// moment/dayjs don't bundle non-English locale data by default; import the
// ones we ship translations for so their .locale() calls below can actually
// switch to them. Some components use moment, others dayjs — both need syncing.
import 'moment/locale/ar';
import 'dayjs/locale/ar';

// The stock 'ar' locale also transliterates digits to Arabic-Indic numerals
// (e.g. "2026" -> "٢٠٢٦"), but the rest of the app (invoice PDFs, amounts,
// IDs) consistently uses Western digits. Override postformat to keep numerals
// as-is so dates stay visually consistent — only month/day names translate.
moment.updateLocale('ar', { postformat: (value: string) => value });

// ─── Bundled baseline translations ──────────────────────────────────────────
// These are the offline-safe fallback. The CDN will override them at runtime.
// To add a new language locally, import its JSON here AND add it to languages.json.
import en from './en.json';
import ar from './ar.json';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LanguageDefinition {
  code: string; // e.g. "en", "ar", "fr", "zh", "de"
  name: string; // display name in that language, e.g. "العربية"
  dir: 'ltr' | 'rtl';
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const LANG_STORAGE_KEY = 'classveew_lang';
const DEFAULT_LANG = 'en';

// ─── Language registry ───────────────────────────────────────────────────────
// Starts with bundled definitions. Gets replaced by CDN/languages.json at runtime.
// IMPORTANT: This list is NOT the source of truth — languages.json is.
// It is only used as the offline fallback.

const BUNDLED_LANGUAGES: LanguageDefinition[] = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
];

// Runtime registry — populated from languages.json; starts with bundled fallback
let languageRegistry: LanguageDefinition[] = [...BUNDLED_LANGUAGES];

/** Return all currently known languages. Used by LanguageSelector. */
export function getAvailableLanguages(): LanguageDefinition[] {
  return languageRegistry;
}

/** Return the LanguageDefinition for a code, or undefined if not found. */
export function getLanguageDef(code: string): LanguageDefinition | undefined {
  return languageRegistry.find((l) => l.code === code);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStoredLang(): string {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    // Validate: must be a non-empty string (actual validity checked against registry later)
    if (stored && stored.trim().length > 0) return stored.trim();
  } catch {
    // localStorage blocked (privacy mode)
  }
  return DEFAULT_LANG;
}

/**
 * Read a `?lang=` (or `&lang=`) query param from the current URL.
 * This is how a backend-generated email link (e.g. ".../verify?token=...&lang=ar")
 * opens the app in the recipient's language — even on a brand-new device where
 * nothing is stored yet. Returns null when absent/empty.
 */
function getUrlLang(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl && fromUrl.trim().length > 0) return fromUrl.trim().toLowerCase();
  } catch {
    // window/URL unavailable (SSR or blocked)
  }
  return null;
}

function persistLang(code: string): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // ignore
  }
}

/**
 * Apply RTL/LTR direction based on the language registry.
 * No hardcoded language codes — looks up the dir from the registry.
 */
export function applyDocumentDir(code: string): void {
  const def = getLanguageDef(code);
  const dir = def?.dir ?? 'ltr'; // unknown language defaults to LTR
  document.documentElement.setAttribute('lang', code);
  document.documentElement.setAttribute('dir', dir);
}

// ─── CDN config ──────────────────────────────────────────────────────────────

// Cast safely — import.meta is Vite-specific
const cdnBase = (import.meta as Record<string, any>).env?.VITE_I18N_CDN_URL as string | undefined;

// ─── Fetch languages.json from CDN and update registry ───────────────────────

async function fetchLanguageRegistry(): Promise<void> {
  if (!cdnBase) return;
  try {
    const url = cdnBase.replace(/\/v\d+\/?$/, '') + '/../languages.json';
    const safeUrl = new URL(url, window.location.origin).toString();
    const response = await fetch(safeUrl, { cache: 'no-store' });
    if (!response.ok) return;
    const defs: LanguageDefinition[] = await response.json();
    if (Array.isArray(defs) && defs.length > 0) {
      languageRegistry = defs;
    }
  } catch {
    // CDN down — keep bundled registry
  }
}

// ─── Build bundled resources (covers all bundled languages) ─────────────────
// Add new bundled translations here if you want offline support for them.
// Languages only in languages.json (not here) will be loaded from CDN only.

const bundledResources: Record<string, { translation: unknown }> = {
  en: { translation: en },
  ar: { translation: ar },
};

// ─── i18next initialisation ──────────────────────────────────────────────────

// Priority: ?lang= in the URL (email link) → persisted choice → default.
// When the URL carries a language, persist it so it sticks for the whole session
// and survives navigation away from the landing page.
const urlLang = getUrlLang();
const initialLang = urlLang ?? getStoredLang();
if (urlLang) persistLang(urlLang);

void i18next
  .use(initReactI18next)
  .use(HttpBackend)
  .init({
    lng: initialLang,
    fallbackLng: DEFAULT_LANG,
    // An empty-string value must NOT render blank — fall back to default lang / key.
    returnEmptyString: false,
    defaultNS: 'translation',
    ns: ['translation'],

    /**
     * Bundled resources are the offline-safe fallback.
     * CDN backend (below) will override these once loaded.
     */
    resources: bundledResources,

    /**
     * Treat bundled resources as PARTIAL so the HttpBackend still fetches the
     * full file from S3/CDN and overrides them at runtime. Without this,
     * i18next sees the bundled language as "loaded" and never calls the
     * backend — meaning S3 translation updates would require a redeploy.
     * With it: bundled = offline fallback, S3 = source of truth (no redeploy).
     * Only enabled when a CDN is configured (otherwise bundled-only, no backend).
     */
    partialBundledLanguages: Boolean(cdnBase),

    /**
     * CDN backend — only active when VITE_I18N_CDN_URL is set.
     * loadPath uses {{lng}} which i18next replaces with the language code.
     * No hardcoded language paths here.
     */
    backend: cdnBase
      ? {
          loadPath: `${cdnBase}/{{lng}}.json`,
          crossDomain: true,
          requestOptions: { cache: 'no-store' },
        }
      : undefined,

    interpolation: { escapeValue: false },

    react: { useSuspense: false },
  })
  .then(() => {
    // After init: fetch CDN language registry so LanguageSelector has the full list
    void fetchLanguageRegistry();
  });

/** Apply document direction immediately (before React mounts) */
applyDocumentDir(initialLang);

/** Sync moment.js's global locale so every date formatted app-wide (invoices,
 *  billing cards, tables, ...) renders month names in the selected language
 *  from the very first render, not just after a manual language toggle. */
moment.locale(initialLang);
/** Same sync for dayjs (used by a handful of components, e.g. the cashier
 *  session modal) — unlike moment's 'ar' locale, dayjs's keeps Western digits
 *  natively, so no postformat override is needed here. */
dayjs.locale(initialLang);


/** JSX-safe, scope-independent translation helper. Returns a string (never an
 *  object), so it can be used in JSX children/attributes and outside React
 *  components. For live language switching inside components prefer useTranslation's t. */
export function tr(key: string, defaultValue?: string): string {
  return String(i18next.t(key, defaultValue ? { defaultValue } : undefined));
}

export { persistLang };
export default i18next;
